import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Versandkosten-Fortschritt auf der PDP (blocks/free-shipping-progress.liquid).
 * Rechnet Warenkorb + gewählte Variante × Menge gegen die Gratis-Versand-Schwelle;
 * liegt das Produkt schon im Warenkorb, nur noch den Warenkorb.
 */
class FreeShippingProgress extends HTMLElement {
  connectedCallback() {
    this.threshold = Number(this.dataset.threshold);
    this.cartTotal = Number(this.dataset.cartTotal);
    this.itemPrice = Number(this.dataset.itemPrice);
    this.inCart = this.hasAttribute('data-in-cart');
    this.section = this.closest('.shopify-section') ?? document;

    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.addEventListener(ThemeEvents.cartUpdate, this.#refreshCart);
    this.section.addEventListener('change', this.#onQuantityChange);
    this.section.addEventListener('input', this.#onQuantityChange);
    this.section.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
    window.addEventListener('pageshow', this.#onPageShow);

    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#refreshCart);
    this.section.removeEventListener('change', this.#onQuantityChange);
    this.section.removeEventListener('input', this.#onQuantityChange);
    this.section.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  get quantity() {
    const input = this.section.querySelector('product-form-component input[name="quantity"]');
    const value = input instanceof HTMLInputElement ? Number(input.value) : 1;
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    const { resource, data } = /** @type {CustomEvent} */ (event).detail ?? {};
    if (data?.productId && data.productId !== this.dataset.productId) return;
    if (resource?.price == null) return;

    this.itemPrice = Number(resource.price);
    this.render();
  };

  /** @param {Event} event */
  #onQuantityChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name === 'quantity') this.render();
  };

  /** @param {Event} event */
  #onQuantitySelectorUpdate = (event) => {
    // Nur der Mengen-Wähler des Produktformulars, nicht die Warenkorb-Zeilen
    if (/** @type {CustomEvent} */ (event).detail?.cartLine) return;
    this.render();
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) this.#refreshCart();
  };

  #refreshCart = async () => {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      this.cartTotal = Number(cart.total_price);
      this.inCart = cart.items.some((/** @type {{product_id: number}} */ item) => String(item.product_id) === this.dataset.productId);
      this.render();
    } catch (error) {
      console.error(error);
    }
  };

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'EUR');
  }

  render() {
    const message = this.querySelector('.free-shipping-bar__message');
    const cartFill = /** @type {HTMLElement | null} */ (this.querySelector('.free-shipping-bar__fill--cart'));
    const itemFill = /** @type {HTMLElement | null} */ (this.querySelector('.free-shipping-bar__fill--item'));
    if (!message || !cartFill || !itemFill || !this.threshold) return;

    // Liegt der Artikel schon im Warenkorb, zählt nur noch der Warenkorb.
    const itemTotal = this.inCart ? 0 : this.itemPrice * this.quantity;
    const total = this.cartTotal + itemTotal;
    const remaining = this.threshold - total;
    const done = remaining <= 0;

    const cartShare = Math.min(100, (this.cartTotal / this.threshold) * 100);
    const itemShare = Math.min(100 - cartShare, (itemTotal / this.threshold) * 100);
    cartFill.style.width = `${cartShare}%`;
    itemFill.style.width = `${itemShare}%`;

    this.classList.toggle('free-shipping-bar--done', done);

    if (this.cartTotal >= this.threshold) {
      message.innerHTML = '<span class="free-shipping-bar__check">✓</span> Dein Versand nach Österreich und Deutschland ist gratis.';
    } else if (this.inCart) {
      message.innerHTML = `Dir fehlen noch <strong>${this.#money(remaining)}</strong> für den kostenlosen Versand.`;
    } else if (done) {
      message.innerHTML =
        '<span class="free-shipping-bar__check">✓</span> Mit diesem Artikel ist dein Versand nach Österreich und Deutschland gratis.';
    } else if (this.cartTotal > 0) {
      message.innerHTML = `Mit diesem Artikel fehlen dir nur noch <strong>${this.#money(remaining)}</strong> für den kostenlosen Versand.`;
    } else {
      message.innerHTML = `Gratis Versand ab <strong>${this.#money(this.threshold)}</strong> – mit diesem Artikel fehlen dir noch <strong>${this.#money(remaining)}</strong>.`;
    }
  }
}

if (!customElements.get('free-shipping-progress')) {
  customElements.define('free-shipping-progress', FreeShippingProgress);
}
